<template>
  <div class="smart-select">
    <b-input-group :class="{ 'has-value': !!value }">
      <b-input-group-prepend class="icon" is-text>
        <feather-icon :icon="icon" />
      </b-input-group-prepend>

      <!-- DESKTOP: vue-select -->
      <v-select
        class="smart-desktop"
        :value="selectedOption"
        @input="onDesktopSelect"
        :options="desktopOptions"
        :label="label"
        :reduce="s => s"
        :selectable="o => !o._header && (!o._disabled || showIncompatible)"
        :filter-by="filterFn"
        :placeholder="placeholder"
      >
        <template #option="o">
          <div v-if="o._header && o._incompatible" class="smart-group-header incompatible-header" @click.stop>
            {{ o[label] }}
            <span class="incompatible-toggle" @click.stop="showIncompatible = !showIncompatible">
              {{ showIncompatible ? 'Restrict' : 'Allow' }}
            </span>
          </div>
          <div v-else-if="o._header" class="smart-group-header">{{ o[label] }}</div>
          <div v-else class="smart-option" :class="{ 'smart-option-disabled': o._disabled && !showIncompatible }">
            <span class="smart-option-name">{{ o[label] }}</span>
            <span v-if="o._disabled && !showIncompatible" class="smart-option-incompatible">Not for {{ brandLabel() }}</span>
            <span v-else-if="price" class="smart-option-price">${{ formatPrice(o.price) }}</span>
          </div>
        </template>
        <template #selected-option="s">
          <span class="smart-sel-name">{{ s[label] }}</span>
        </template>
        <template #no-options>
          <div v-if="!list" class="smart-loading">Loading ...</div>
          <div v-else>No results</div>
        </template>
      </v-select>

      <!-- MOBILE: trigger -->
      <div
        class="smart-mobile-trigger form-control"
        :class="{ 'is-placeholder': !value }"
        @click="openSheet"
      >
        <span v-if="selectedOption" class="trigger-text">
          <span class="trigger-name">{{ selectedOption[label] }}</span>
        </span>
        <span v-else>{{ placeholder }}</span>
        <svg class="trigger-arrow" width="10" height="6" viewBox="0 0 10 6">
          <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>
        </svg>
      </div>
    </b-input-group>
    <span v-if="selectedOption && price" class="smart-price-hint">
      ${{ formatPrice(selectedOption.price) }}
    </span>

    <!-- MOBILE: bottom sheet -->
    <div class="smart-sheet-overlay" :class="{ open: sheetOpen }" @click="closeSheet"></div>
    <div class="smart-sheet" :class="{ open: sheetOpen, 'keyboard-up': searchFocused }">
      <div v-if="!searchFocused" class="smart-sheet-handle"></div>
      <div class="smart-sheet-header">
        <h3>{{ placeholder }}</h3>
        <button type="button" class="smart-sheet-close" @click="closeSheet">&times;</button>
      </div>
      <input
        ref="sheetSearch"
        class="smart-sheet-search"
        v-model="search"
        placeholder="Search..."
        type="text"
        @focus="searchFocused = true"
        @blur="onSearchBlur"
      >
      <div class="smart-sheet-list">
        <template v-for="(item, i) in sheetFiltered">
          <div v-if="item._header && item._incompatible" :key="'sh'+i" class="smart-sheet-group incompatible-header">
            {{ item[label] }}
            <span class="incompatible-toggle" @click.stop="showIncompatible = !showIncompatible">
              {{ showIncompatible ? 'Restrict' : 'Allow' }}
            </span>
          </div>
          <div v-else-if="item._header" :key="'sh'+i" class="smart-sheet-group">
            {{ item[label] }}
          </div>
          <div
            v-else
            :key="item._uid || item.id"
            class="smart-sheet-item"
            :class="{ selected: value && value == item.id, 'sheet-item-disabled': item._disabled && !showIncompatible }"
            @click="onSheetSelect(item)"
          >
            <div class="sheet-item-left">
              <span class="sheet-item-name">{{ item[label] }}</span>
              <span v-if="item._disabled && !showIncompatible" class="sheet-item-sub sheet-item-incompatible">Not available for {{ brandLabel() }}</span>
              <span v-else-if="item.sub" class="sheet-item-sub">{{ item.sub }}</span>
            </div>
            <div class="sheet-item-right">
              <span v-if="(!item._disabled || showIncompatible) && price" class="sheet-item-price">${{ formatPrice(item.price) }}</span>
              <div v-if="value && value == item.id" class="sheet-item-check">&#10003;</div>
            </div>
          </div>
        </template>
        <div v-if="sheetFilteredCount === 0" class="smart-sheet-empty">No services found</div>
      </div>
    </div>
  </div>
</template>

<script>
import { BInputGroup, BInputGroupPrepend } from "bootstrap-vue";
import vSelect from "vue-select";

const STORAGE_KEY = "serviceUsage";
const MAX_TOP = 5;

export function trackServiceUsage(serviceId) {
  if (!serviceId) return;
  try {
    const usage = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    const key = String(serviceId);
    const entry = usage[key] || { count: 0, last: 0 };
    entry.count++;
    entry.last = Date.now();
    usage[key] = entry;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(usage));
  } catch (e) { /* silent */ }
}

export default {
  name: "SmartSelect",
  props: {
    value: { default: null },
    list: { default: null },
    icon: { type: String, default: "LayersIcon" },
    placeholder: { type: String, default: "Select..." },
    label: { type: String, default: "name" },
    price: { type: Boolean, default: false },
    brand: { type: String, default: null },
  },
  data() {
    return { sheetOpen: false, search: "", usage: {}, showIncompatible: false, searchFocused: false };
  },
  created() { this.loadUsage(); },
  watch: {
    brand(newBrand) {
      this.showIncompatible = false;
      // Reset selection if current service is incompatible with new brand
      if (newBrand && this.value && this.list) {
        const current = this.list.find(s => s.id == this.value);
        if (current && !this.isCompatible(current)) {
          this.$emit("input", null);
        }
      }
    },
  },
  computed: {
    selectedOption() {
      if (!this.value || !this.list) return null;
      return this.list.find((s) => s.id == this.value) || null;
    },
    topIds() {
      const usage = this.usage;
      return Object.keys(usage)
        .map((id) => ({ id: parseInt(id), count: usage[id].count, last: usage[id].last }))
        .sort((a, b) => b.count - a.count || b.last - a.last)
        .slice(0, MAX_TOP)
        .map((x) => x.id);
    },
    // Brand-filtered list: compatible first, incompatible at bottom with _disabled
    sortedList() {
      if (!this.list) return [];
      if (!this.brand) return this.list.map(s => ({ ...s, _disabled: false }));
      const compatible = [];
      const incompatible = [];
      for (const s of this.list) {
        if (this.isCompatible(s)) {
          compatible.push({ ...s, _disabled: false });
        } else {
          incompatible.push({ ...s, _disabled: true });
        }
      }
      return [...compatible, ...incompatible];
    },
    desktopOptions() {
      if (!this.sortedList.length) return [];
      const all = this.sortedList;
      const topItems = this.topIds.map((id) => all.find((s) => s.id === id)).filter(Boolean);
      const rest = all.filter((s) => this.topIds.indexOf(s.id) === -1);
      const options = [];
      // Only show Most Used from compatible items
      const compatibleTop = topItems.filter(s => !s._disabled);
      if (compatibleTop.length) {
        options.push({ [this.label]: "★ Most Used", _header: true });
        compatibleTop.forEach((s) => options.push(s));
        options.push({ [this.label]: "All Services", _header: true });
      }
      // Compatible rest
      const compatibleRest = rest.filter(s => !s._disabled);
      compatibleRest.forEach((s) => options.push(s));
      // Incompatible at bottom
      const disabled = all.filter(s => s._disabled);
      if (disabled.length) {
        options.push({ [this.label]: "Not Compatible", _header: true, _incompatible: true });
        disabled.forEach((s) => options.push(s));
      }
      return options;
    },
    sheetOptions() {
      if (!this.sortedList.length) return [];
      const all = this.sortedList;
      const topItems = this.topIds
        .map((id) => all.find((s) => s.id === id)).filter(Boolean)
        .filter(s => !s._disabled)
        .map((s) => ({ ...s, _uid: "top_" + s.id }));
      const options = [];
      if (topItems.length) {
        options.push({ [this.label]: "★ Most Used", _header: true });
        topItems.forEach((s) => options.push(s));
      }
      // All compatible
      const compatible = all.filter(s => !s._disabled);
      options.push({ [this.label]: "All Services", _header: true });
      compatible.forEach((s) => options.push(s));
      // Incompatible at bottom
      const disabled = all.filter(s => s._disabled);
      if (disabled.length) {
        options.push({ [this.label]: "Not Compatible", _header: true, _incompatible: true });
        disabled.forEach((s) => options.push(s));
      }
      return options;
    },
    sheetFiltered() {
      const q = this.search.toLowerCase().trim();
      if (!q) return this.sheetOptions;
      // When searching: flat list, no headers, no duplicates
      const seen = new Set();
      const result = [];
      for (const item of this.sheetOptions) {
        if (item._header) continue;
        if (seen.has(item.id)) continue;
        const match = (item[this.label] || "").toLowerCase().includes(q) ||
          (item.sub || "").toLowerCase().includes(q);
        if (match) {
          seen.add(item.id);
          result.push(item);
        }
      }
      return result;
    },
    sheetFilteredCount() { return this.sheetFiltered.filter((i) => !i._header).length; },
  },
  methods: {
    isCompatible(service) {
      if (!this.brand) return true;
      const sub = (service.sub || "").toLowerCase();
      if (this.brand === "apple") return sub.includes("apple") || sub.includes("all brands");
      if (this.brand === "samsung") return sub.includes("samsung") || sub.includes("all brands");
      // others — only All Brands
      return sub.includes("all brands");
    },
    loadUsage() {
      try { this.usage = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
      catch (e) { this.usage = {}; }
    },
    refreshUsage() { this.loadUsage(); },
    formatPrice(p) { return Number.parseFloat(p).toFixed(2); },
    brandLabel() {
      if (this.brand === "apple") return "Apple";
      if (this.brand === "samsung") return "Samsung";
      return this.brand;
    },
    filterFn(option, label, search) {
      if (option._header) return false;
      const q = search.toLowerCase();
      return (option[this.label] || "").toLowerCase().includes(q) ||
        (option.sub || "").toLowerCase().includes(q);
    },
    onDesktopSelect(item) {
      if (item && item._disabled && !this.showIncompatible) return;
      this.$emit("input", item ? item.id : null);
    },
    openSheet() {
      this.search = "";
      this.sheetOpen = true;
      document.body.style.overflow = "hidden";
    },
    closeSheet() {
      this.sheetOpen = false;
      this.searchFocused = false;
      if (this._blurTimer) { clearTimeout(this._blurTimer); this._blurTimer = null; }
      document.body.style.overflow = "";
    },
    onSearchBlur() {
      // Delay so tap on sheet item registers before sheet repositions
      this._blurTimer = setTimeout(() => { this.searchFocused = false; }, 150);
    },
    onSheetSelect(item) {
      if (item._disabled && !this.showIncompatible) return;
      this.$emit("input", item.id);
      this.closeSheet();
    },
  },
  components: { vSelect, BInputGroup, BInputGroupPrepend },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.smart-select {
  .input-group {
    .icon { z-index: 4; }
    // Icon collapse when has value
    .input-group-text {
      transition: width 0.25s, padding 0.25s, opacity 0.2s, border 0.2s;
      overflow: hidden;
      white-space: nowrap;
    }
    &.has-value .input-group-text {
      width: 0 !important;
      padding: 0 !important;
      border: none !important;
      opacity: 0;
    }
    &.has-value .smart-desktop .vs__dropdown-toggle {
      border-left: 1px solid #d8d6de;
      border-radius: 7px;
    }
    &.has-value .smart-mobile-trigger {
      border-left: 1px solid #d8d6de;
      border-radius: 7px;
    }
  }

  /* ========== DESKTOP ========== */
  .smart-desktop {
    flex: 1;
    height: 37px;
    background: transparent;
    padding: 0;
    border: none;

    .vs__dropdown-toggle {
      border-left: none;
      border-radius: 0 7px 7px 0;
      box-shadow: none !important;
      height: 100%;
    }
    .vs__search {
      text-align: center;
      position: absolute;
      left: 0;
      top: -3px;
      width: 100%;
      height: 110%;
    }
    .vs__selected {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #1a1a1a;
    }
    &.vs--open .vs__selected {
      opacity: 0;
    }
    .vs__dropdown-menu {
      border-radius: 8px;
      margin-top: 4px;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
      max-height: 320px;
      padding: 4px;
    }
    .vs__dropdown-option { padding: 0; border-radius: 6px; }
    .vs__dropdown-option--highlight { background: rgba(115, 103, 240, 0.08); }
    .vs__dropdown-option--disabled { opacity: 1; background: none; }
    .vs__actions svg { fill: #acacac; }
  }
  .smart-group-header {
    font-size: 0.65rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.8px; color: #7367f0; padding: 12px 12px 6px;
    border-bottom: 1px solid rgba(115, 103, 240, 0.12);
    margin: 4px 4px 2px;
  }
  .smart-option {
    display: flex; justify-content: space-between; align-items: center;
    width: 100%; padding: 8px 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
  .smart-option-name { font-weight: 500; font-size: 0.86rem; color: #1a1a1a; }
  .smart-option-price { color: #7367f0; font-weight: 600; font-size: 0.84rem; white-space: nowrap; margin-left: 8px; }
  .smart-option-disabled {
    opacity: 0.45;
    cursor: not-allowed;
    .smart-option-name { color: #999; }
  }
  .smart-option-incompatible {
    font-size: 0.72rem; color: #ea5455; font-weight: 500;
    white-space: nowrap; margin-left: 8px;
  }
  .incompatible-header { color: #ea5455 !important; border-bottom-color: rgba(234, 84, 85, 0.15) !important; }
  .incompatible-toggle {
    float: right;
    font-size: 0.65rem;
    font-weight: 600;
    color: #7367f0;
    cursor: pointer;
    text-transform: none;
    letter-spacing: 0;
    padding: 1px 8px;
    border-radius: 8px;
    background: rgba(115, 103, 240, 0.08);
    transition: background .15s;
    &:hover { background: rgba(115, 103, 240, 0.16); }
  }
  .smart-sel-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
  }
  .smart-loading { text-align: center; padding: 10px; color: #b9b9c3; }

  // Price hint — subtle badge, bottom right under the select
  .smart-price-hint {
    display: inline-block;
    float: right;
    margin-top: 5px;
    margin-bottom: 0;
    font-size: 0.7rem;
    font-weight: 600;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.08);
    padding: 2px 10px;
    border-radius: 10px;
    letter-spacing: 0.3px;
  }
  // Clearfix to prevent float from escaping
  &::after {
    content: "";
    display: table;
    clear: both;
  }

  /* ========== MOBILE TRIGGER ========== */
  .smart-mobile-trigger {
    display: none;
    align-items: center; justify-content: center;
    border-left: none;
    text-align: center;
    cursor: pointer; gap: 6px;
    color: #1a1a1a;
    -webkit-tap-highlight-color: transparent;
    &.is-placeholder { color: #b9b9c3; }
    .trigger-text {
      display: flex; align-items: center; flex: 1; min-width: 0;
      overflow: hidden; justify-content: center;
    }
    .trigger-name {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      min-width: 0;
    }
    .trigger-arrow { color: #acacac; flex-shrink: 0; margin-left: 8px; }
  }
  @media (max-width: 767.98px) {
    .smart-desktop { display: none !important; }
    .smart-mobile-trigger { display: flex !important; }
  }

  /* ========== BOTTOM SHEET ========== */
  .smart-sheet-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.45);
    z-index: 9998; opacity: 0; visibility: hidden;
    transition: opacity .25s, visibility .25s;
    &.open { opacity: 1; visibility: visible; }
  }
  .smart-sheet {
    position: fixed; bottom: 0; left: 0; right: 0; z-index: 9999;
    background: #fff; border-radius: 16px 16px 0 0;
    max-height: 88vh; display: flex; flex-direction: column;
    transform: translateY(100%);
    transition: transform .35s cubic-bezier(.32,.72,0,1), max-height .35s ease, border-radius .35s ease, padding-top .35s ease;
    &.open { transform: translateY(0); }
    &.open.keyboard-up {
      transform: translateY(calc(-100vh + 100%));
      border-radius: 0 0 16px 16px;
      padding-top: env(safe-area-inset-top, 0px);
      max-height: 70vh;
    }
  }
  .smart-sheet-handle { width: 36px; height: 4px; background: #d0d0d0; border-radius: 2px; margin: 10px auto 0; }
  .smart-sheet-header {
    display: flex; align-items: center; justify-content: space-between; padding: 14px 18px 10px;
    h3 { font-size: 1rem; font-weight: 600; margin: 0; }
  }
  .smart-sheet-close { background: none; border: none; font-size: 1.5rem; color: #999; cursor: pointer; padding: 4px 8px; line-height: 1; }
  .smart-sheet-search {
    margin: 0 16px 8px; padding: 10px 14px;
    border: 1.5px solid #e0e0e0; border-radius: 10px;
    font-size: 0.9rem; width: calc(100% - 32px); outline: none;
    background: #fff; color: #1a1a1a;
    &:focus { border-color: #7367f0; }
  }
  .smart-sheet-list { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; padding: 4px 8px 28px; }
  .smart-sheet-group {
    font-size: 0.65rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.8px; color: #7367f0; padding: 14px 12px 6px;
    border-bottom: 1px solid rgba(115, 103, 240, 0.12);
    margin: 0 4px;
    &:first-child { padding-top: 6px; }
  }
  .smart-sheet-item {
    display: flex; align-items: center; justify-content: space-between;
    padding: 13px 12px; border-radius: 10px; cursor: pointer;
    transition: background .15s; -webkit-tap-highlight-color: transparent;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    &:active, &.selected { background: rgba(115, 103, 240, 0.08); }
  }
  .sheet-item-left { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
  .sheet-item-name { font-weight: 500; font-size: 0.92rem; color: #1a1a1a; }
  .sheet-item-sub { font-size: 0.78rem; color: #888; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sheet-item-incompatible { color: #ea5455 !important; font-weight: 500; }
  .sheet-item-disabled {
    opacity: 0.45;
    cursor: default;
    -webkit-tap-highlight-color: transparent;
    &:active { background: none !important; }
    .sheet-item-name { color: #999; }
  }
  .sheet-item-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
  .sheet-item-price { color: #7367f0; font-weight: 600; font-size: 0.88rem; }
  .sheet-item-check {
    width: 22px; height: 22px; border-radius: 50%;
    background: #7367f0; color: #fff; font-size: 0.7rem;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .smart-sheet-empty { text-align: center; padding: 30px; color: #aaa; }

  // Desktop: bigger, clearer service select
  @media (min-width: 768px) {
    .smart-desktop {
      height: 46px;
      font-size: 1.05rem;
    }
    .smart-sel-name {
      font-size: 1.05rem;
      font-weight: 600;
    }
    .vs__search {
      font-size: 1.05rem !important;
    }
    .input-group-text {
      padding-top: 10px;
      padding-bottom: 10px;
    }
    .vs__dropdown-menu {
      min-width: 420px;
      max-height: 380px;
    }
    .smart-option {
      padding: 10px 12px;
    }
    .smart-option-name {
      font-size: 0.95rem;
    }
    .smart-option-price {
      font-size: 0.92rem;
    }
    .smart-group-header {
      font-size: 0.7rem;
    }
  }
}

/* ========== DARK MODE ========== */
.dark-layout .smart-select {
  .input-group.has-value .smart-desktop .vs__dropdown-toggle {
    border-left-color: #404656;
  }
  .input-group.has-value .smart-mobile-trigger {
    border-left-color: #404656;
  }
  .smart-desktop {
    .vs__dropdown-toggle { border-color: #404656; }
    .vs__dropdown-toggle:focus-within { border-color: #7367f0; }
    .vs__dropdown-menu { background: #283046; border-color: #404656; }
    .vs__search { color: #d0d2d6; }
    .vs__selected { color: #d0d2d6; }
    .vs__actions svg { fill: #676d7d; }
  }
  .smart-group-header { color: #9f97f7; border-bottom-color: rgba(115, 103, 240, 0.2); }
  .smart-option-name { color: #d0d2d6; }
  .smart-mobile-trigger {
    color: #d0d2d6;
    background-color: #283046;
    border-color: #404656;
    &.is-placeholder { color: #676d7d; }
    .trigger-arrow { color: #676d7d; }
  }
  .smart-sheet, .smart-sheet-search { background: #283046; color: #d0d2d6; }
  .smart-sheet-search { border-color: #404656; &:focus { border-color: #7367f0; } }
  .smart-sheet-header h3 { color: #d0d2d6; }
  .smart-sheet-close { color: #676d7d; }
  .smart-sheet-group { color: #9f97f7; border-bottom-color: rgba(115, 103, 240, 0.2); }
  .sheet-item-name { color: #d0d2d6; }
  .sheet-item-sub { color: #676d7d; }
  .smart-sheet-item { &:active, &.selected { background: rgba(115, 103, 240, 0.15); } }
  .smart-option { border-bottom-color: rgba(255, 255, 255, 0.08); }
  .smart-sheet-item { border-bottom-color: rgba(255, 255, 255, 0.08); }
  .smart-price-hint { color: #9f97f7; background: rgba(115, 103, 240, 0.15); }
  .incompatible-header { color: #ea5455 !important; border-bottom-color: rgba(234, 84, 85, 0.2) !important; }
  .incompatible-toggle { color: #9f97f7; background: rgba(115, 103, 240, 0.15); }
  .smart-option-disabled .smart-option-name { color: #555; }
  .smart-option-incompatible { color: #ea5455; }
  .sheet-item-disabled .sheet-item-name { color: #555; }
  .sheet-item-incompatible { color: #ea5455 !important; }
}
</style>